import { Box, Button, Column, Flex, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { useFormContext } from 'react-hook-form';
import { useSaveWorkerType } from '~/domain/SaveWorkerType';
import { fireWorkerTypeSubmitted } from '~/posthogs/events/Signup';
import { useAuthFormSubmit } from '../../shared';
import type { ISignUpFormValues } from '../types';
import { DISCLAIMER } from './constants';
import { EducationalContent, FaqSection, PsaSection, WorkerTypeSelection } from './sections';

const CONTENT_WIDTH = 774;
const CTA_WIDTH = 305;

export interface IWorkerTypeFormProps {
  onSuccess: () => void;
}

export const WorkerTypeForm = ({ onSuccess }: IWorkerTypeFormProps) => {
  const { mutateAsync: saveWorkerType, isPending } = useSaveWorkerType();
  const {
    getValues,
    reset,
    formState: { dirtyFields },
  } = useFormContext<ISignUpFormValues>();

  const { submit, bannerVisible, bannerRef } = useAuthFormSubmit<ISignUpFormValues>({
    onValid: async ({ workerType, psaAccepted }) => {
      fireWorkerTypeSubmitted();
      const userEdited = Object.keys(dirtyFields).length > 0;
      if (!userEdited) {
        onSuccess();
        return;
      }
      await saveWorkerType({
        workerType: workerType as 'contractor' | 'employee' | 'none',
        psaAccepted: workerType === 'employee' ? psaAccepted : undefined,
      });
      reset(getValues());
      onSuccess();
    },
  });

  return (
    <Box style={{ width: CONTENT_WIDTH, margin: '0 auto' }}>
      <form onSubmit={submit} data-testid="worker-type-form" noValidate>
        <Column gap={8}>
          <EducationalContent />
          <WorkerTypeSelection bannerVisible={bannerVisible} bannerRef={bannerRef} />
          <Typography
            as="p"
            color={colors.neutral[500]}
            textAlign="center"
            italic
            style={{ margin: 0, lineHeight: 1.5, marginTop: -12 }}
          >
            {DISCLAIMER}
          </Typography>
          <PsaSection />
          <Flex justify="center">
            <Box style={{ width: CTA_WIDTH, marginTop: 8 }}>
              <Button
                data-cy="btn-worker-type-continue"
                type="submit"
                variant="primary"
                size="lg"
                isLoading={isPending}
                style={{ width: '100%' }}
              >
                Let’s Get Started
              </Button>
            </Box>
          </Flex>
          <FaqSection />
        </Column>
      </form>
    </Box>
  );
};
